import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

// Terms of Sale Component
export const TermsOfSale = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-charcoal">Terms of Sale</CardTitle>
            <p className="text-gray-600">Last updated: January 2025</p>
          </CardHeader>
          <CardContent className="prose prose-lg max-w-none">
            <h2>1. Introduction</h2>
            <p>
              These Terms of Sale ("Terms") govern the purchase of services and products offered by Shoppers Discount Club 
              ("we", "us", "our") through our website and platform. By making a purchase, you agree to these Terms.
            </p>

            <h2>2. Business Listings and Directory Services</h2>
            <p>
              We provide business directory and promotional services including:
            </p>
            <ul>
              <li>Business profile listings in our online directory</li>
              <li>Promotional features including QR code discount systems</li>
              <li>Enhanced visibility through featured listing packages</li>
              <li>Job posting and event listing services</li>
              <li>AI chatbot integration for customer engagement</li>
            </ul>

            <h2>3. Pricing and Payment</h2>
            <p>
              All prices are displayed in British Pounds (GBP) and include applicable VAT where required. 
              Payment is due immediately upon purchase unless otherwise specified. We accept major credit 
              cards and PayPal through secure payment processors.
            </p>

            <h2>4. Service Delivery</h2>
            <p>
              Services will be activated within 48 hours of confirmed payment. Business listings will 
              remain active for the duration specified in your chosen package. QR code systems and 
              promotional features will be configured according to your package terms.
            </p>

            <h2>5. Refund & Cancellation Policy</h2>
            
            <h3>No Refunds</h3>
            <p>
              Shoppers Discount Club operates on a monthly subscription basis. Once a payment has been made, 
              no refunds will be issued.
            </p>

            <h3>Cancellations</h3>
            <p>
              You may request cancellation of your subscription by providing 14 days' notice in advance of 
              your next billing date. Upon cancellation, your directory entry and services will be stopped, 
              and no further monthly payments will be collected unless you choose to reactivate.
            </p>

            <h3>Missed Payments</h3>
            <p>
              If your monthly subscription payment is missed or fails, your online chatbot app and directory 
              features will be suspended until payment is received. Access will be restored once the 
              outstanding balance is cleared.
            </p>

            <h3>Reactivation</h3>
            <p>
              If you wish to restart your subscription after cancellation or suspension, you may do so at 
              any time by reactivating your account and resuming monthly payments.
            </p>

            <h2>6. Service Modifications</h2>
            <p>
              We reserve the right to modify our services and pricing with reasonable notice. 
              Existing customers will be notified of significant changes that may affect their service.
            </p>

            <h2>7. Limitation of Liability</h2>
            <p>
              Our liability is limited to the amount paid for services. We are not responsible for 
              indirect damages, loss of revenue, or business interruption resulting from our services.
            </p>

            <h2>8. Contact Information</h2>
            <p>
              For questions about these Terms of Sale, contact us at order@shoppersdiscount.club
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Terms of Use Component
export const TermsOfUse = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-charcoal">Terms of Use</CardTitle>
            <p className="text-gray-600">Last updated: January 2025</p>
          </CardHeader>
          <CardContent className="prose prose-lg max-w-none">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing and using the Shoppers Discount Club website and services, you accept and agree 
              to be bound by these Terms of Use. If you do not agree to these terms, please do not use our services.
            </p>

            <h2>2. Description of Service</h2>
            <p>
              Shoppers Discount Club is a UK-based business directory platform that connects consumers with 
              local businesses through real-time offers, QR code discounts, job listings, and community events.
            </p>

            <h2>3. User Responsibilities</h2>
            <p>You agree to:</p>
            <ul>
              <li>Provide accurate and truthful information when registering or submitting business details</li>
              <li>Maintain the security of your account credentials</li>
              <li>Use our platform in compliance with all applicable UK laws and regulations</li>
              <li>Respect intellectual property rights and not infringe on copyrights or trademarks</li>
              <li>Not engage in fraudulent, harmful, or disruptive activities</li>
            </ul>

            <h2>4. Business Listings</h2>
            <p>
              Businesses may submit applications for directory inclusion. We reserve the right to approve 
              or decline listings based on our quality standards and community guidelines. All business 
              information must be accurate, current, and legally compliant.
            </p>

            <h2>5. QR Code and Discount Systems</h2>
            <p>
              Our QR code discount system is designed to provide real-time offers to consumers. Businesses 
              are responsible for honoring advertised discounts and maintaining accurate offer information.
            </p>

            <h2>6. User Content and Privacy</h2>
            <p>
              Users may submit reviews, comments, and business information. By submitting content, you grant 
              us a license to use, modify, and display such content. See our Privacy Policy for data handling details.
            </p>

            <h2>7. Prohibited Activities</h2>
            <p>The following activities are strictly prohibited:</p>
            <ul>
              <li>Posting false, misleading, or defamatory content</li>
              <li>Attempting to compromise system security</li>
              <li>Spamming or sending unsolicited communications</li>
              <li>Violating any applicable laws or regulations</li>
            </ul>

            <h2>8. Intellectual Property</h2>
            <p>
              All content, trademarks, and intellectual property on our platform remain our property or 
              that of our licensors. Users may not reproduce or distribute our content without permission.
            </p>

            <h2>9. Service Availability</h2>
            <p>
              While we strive for continuous service availability, we do not guarantee uninterrupted access. 
              We may temporarily suspend services for maintenance or improvements.
            </p>

            <h2>10. Termination</h2>
            <p>
              We may terminate or suspend accounts for violations of these terms or for any reason with 
              reasonable notice. Users may terminate their accounts at any time.
            </p>

            <h2>11. Governing Law</h2>
            <p>
              These terms are governed by the laws of England and Wales. Any disputes will be resolved 
              through UK courts or alternative dispute resolution mechanisms.
            </p>

            <h2>12. Contact Information</h2>
            <p>
              For questions about these Terms of Use, contact us at order@shoppersdiscount.club
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Privacy Policy Component
export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-charcoal">Privacy Statement</CardTitle>
            <p className="text-gray-600">Last updated: January 2025</p>
          </CardHeader>
          <CardContent className="prose prose-lg max-w-none">
            <h2>1. Introduction</h2>
            <p>
              Shoppers Discount Club respects your privacy and is committed to protecting your personal data. 
              This privacy policy explains how we collect, use, and safeguard your information when you use our services.
            </p>

            <h2>2. Data We Collect</h2>
            <p>We may collect the following types of information:</p>
            <ul>
              <li><strong>Personal Information:</strong> Name, email address, phone number, business details</li>
              <li><strong>Location Data:</strong> Approximate location for local business recommendations</li>
              <li><strong>Usage Data:</strong> How you interact with our platform, search queries, preferences</li>
              <li><strong>Technical Data:</strong> IP address, browser type, device information</li>
              <li><strong>Marketing Data:</strong> Your preferences for receiving marketing communications</li>
            </ul>

            <h2>3. How We Use Your Data</h2>
            <p>We use your personal data to:</p>
            <ul>
              <li>Provide and maintain our directory and discount services</li>
              <li>Process business applications and manage listings</li>
              <li>Send relevant offers and promotions based on your location and preferences</li>
              <li>Improve our services through analytics and user feedback</li>
              <li>Comply with legal obligations and prevent fraud</li>
              <li>Communicate with you about our services and updates</li>
            </ul>

            <h2>4. Legal Basis for Processing</h2>
            <p>
              Under UK GDPR, we process your data based on:
            </p>
            <ul>
              <li><strong>Consent:</strong> When you agree to receive marketing communications</li>
              <li><strong>Contract:</strong> To provide services you've requested</li>
              <li><strong>Legitimate Interest:</strong> To improve our services and prevent fraud</li>
              <li><strong>Legal Obligation:</strong> To comply with applicable laws</li>
            </ul>

            <h2>5. Data Sharing</h2>
            <p>We may share your data with:</p>
            <ul>
              <li><strong>Business Partners:</strong> To provide discount offers and services</li>
              <li><strong>Service Providers:</strong> Payment processors, hosting providers, analytics services</li>
              <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
            </ul>
            <p>We do not sell personal data to third parties for marketing purposes.</p>

            <h2>6. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal data 
              against unauthorized access, alteration, disclosure, or destruction. This includes encryption, 
              secure servers, and regular security assessments.
            </p>

            <h2>7. Data Retention</h2>
            <p>
              We retain personal data only as long as necessary for the purposes outlined in this policy 
              or as required by law. Business listings remain active based on service agreements.
            </p>

            <h2>8. Your Rights</h2>
            <p>Under UK data protection law, you have the right to:</p>
            <ul>
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request erasure of your data</li>
              <li>Object to processing</li>
              <li>Data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>

            <h2>9. Cookies and Tracking</h2>
            <p>
              We use cookies and similar technologies to enhance your experience, analyze usage, 
              and provide personalized content. You can control cookie settings through your browser.
            </p>

            <h2>10. International Transfers</h2>
            <p>
              Your data may be transferred to and processed in countries outside the UK. We ensure 
              adequate protection through appropriate safeguards and compliance mechanisms.
            </p>

            <h2>11. Children's Privacy</h2>
            <p>
              Our services are not directed to children under 16. We do not knowingly collect 
              personal information from children under this age.
            </p>

            <h2>12. Contact Us</h2>
            <p>
              For privacy-related questions or to exercise your rights, contact us at:
              <br />Email: order@shoppersdiscount.club
              <br />Subject: Privacy Inquiry
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// GDPR Compliance Component
export const GDPRCompliance = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-charcoal">GDPR Compliance</CardTitle>
            <p className="text-gray-600">Last updated: January 2025</p>
          </CardHeader>
          <CardContent className="prose prose-lg max-w-none">
            <h2>1. Our Commitment to GDPR</h2>
            <p>
              Shoppers Discount Club is committed to full compliance with the UK General Data Protection 
              Regulation (UK GDPR) and the Data Protection Act 2018. We respect your privacy rights and 
              maintain transparent data processing practices.
            </p>

            <h2>2. Lawful Basis for Processing</h2>
            <p>
              All personal data processing is based on one or more of the following lawful bases:
            </p>
            <ul>
              <li><strong>Article 6(1)(a) - Consent:</strong> Marketing communications, newsletter subscriptions</li>
              <li><strong>Article 6(1)(b) - Contract:</strong> Business listings, payment processing, service delivery</li>
              <li><strong>Article 6(1)(c) - Legal Obligation:</strong> Tax records, compliance reporting</li>
              <li><strong>Article 6(1)(f) - Legitimate Interest:</strong> Fraud prevention, service improvement, analytics</li>
            </ul>

            <h2>3. Data Subject Rights</h2>
            <p>Under UK GDPR, you have the following rights:</p>
            <ul>
              <li><strong>Right of Access (Article 15):</strong> Request copies of your personal data</li>
              <li><strong>Right to Rectification (Article 16):</strong> Correct inaccurate or incomplete data</li>
              <li><strong>Right to Erasure (Article 17):</strong> Request deletion of your personal data</li>
              <li><strong>Right to Restrict Processing (Article 18):</strong> Limit how we use your data</li>
              <li><strong>Right to Data Portability (Article 20):</strong> Receive your data in a structured format</li>
              <li><strong>Right to Object (Article 21):</strong> Object to processing based on legitimate interest</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for consent-based processing</li>
            </ul>

            <h2>4. Exercising Your Rights</h2>
            <p>
              To exercise any of your rights, please contact us at order@shoppersdiscount.club with 
              "GDPR Request" in the subject line. We will respond within one month of receiving your request.
            </p>

            <h2>5. Data Processing Records</h2>
            <p>We maintain detailed records of all data processing activities, including:</p>
            <ul>
              <li>Purposes of processing</li>
              <li>Categories of data subjects and personal data</li>
              <li>Recipients or categories of recipients</li>
              <li>Data retention periods</li>
              <li>Security measures implemented</li>
            </ul>

            <h2>6. Data Protection Impact Assessments</h2>
            <p>
              We conduct Data Protection Impact Assessments (DPIAs) for high-risk processing activities 
              to identify and mitigate privacy risks before implementing new systems or processes.
            </p>

            <h2>7. Data Breach Procedures</h2>
            <p>
              In the event of a personal data breach, we will:
            </p>
            <ul>
              <li>Notify the ICO within 72 hours if the breach poses a risk</li>
              <li>Inform affected individuals without undue delay if there's a high risk</li>
              <li>Document all breaches and remedial actions taken</li>
              <li>Implement measures to prevent future breaches</li>
            </ul>

            <h2>8. Third-Party Data Processing</h2>
            <p>
              All third-party processors are carefully vetted and bound by Data Processing Agreements (DPAs) 
              that ensure GDPR compliance. We regularly audit our processors to ensure ongoing compliance.
            </p>

            <h2>9. International Data Transfers</h2>
            <p>
              When transferring data outside the UK, we use:
            </p>
            <ul>
              <li>Adequacy decisions by the UK government</li>
              <li>Standard Contractual Clauses (SCCs)</li>
              <li>Binding Corporate Rules where applicable</li>
              <li>Additional safeguards as required</li>
            </ul>

            <h2>10. Staff Training and Awareness</h2>
            <p>
              All staff receive regular GDPR training covering:
            </p>
            <ul>
              <li>Data protection principles</li>
              <li>Individual rights</li>
              <li>Security procedures</li>
              <li>Breach response protocols</li>
            </ul>

            <h2>11. Complaints and Supervisory Authority</h2>
            <p>
              If you're unsatisfied with how we handle your personal data, you have the right to 
              lodge a complaint with the Information Commissioner's Office (ICO):
              <br />Website: ico.org.uk
              <br />Phone: 0303 123 1113
            </p>

            <h2>12. Contact Our Data Protection Officer</h2>
            <p>
              For GDPR-related inquiries, contact us at:
              <br />Email: order@shoppersdiscount.club
              <br />Subject: GDPR Compliance Inquiry
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Risk Disclosure Component
export const RiskDisclosure = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-charcoal">Risk Disclosure Statement</CardTitle>
            <p className="text-gray-600">Last updated: January 2025</p>
          </CardHeader>
          <CardContent className="prose prose-lg max-w-none">
            <h2>1. Platform Usage Risks</h2>
            <p>
              By using Shoppers Discount Club services, you acknowledge and accept the following risks:
            </p>

            <h3>Business Listing Risks</h3>
            <ul>
              <li><strong>Accuracy of Information:</strong> While we strive to maintain accurate business information, 
              we cannot guarantee the completeness or accuracy of all listings</li>
              <li><strong>Business Operations:</strong> Listed businesses operate independently; we are not responsible 
              for their products, services, or business practices</li>
              <li><strong>Availability:</strong> Business hours, offers, and availability may change without notice</li>
            </ul>

            <h3>Discount and Offer Risks</h3>
            <ul>
              <li><strong>Offer Validity:</strong> Discounts and promotions are subject to terms set by individual businesses</li>
              <li><strong>QR Code Technology:</strong> Technical issues may occasionally prevent QR code functionality</li>
              <li><strong>Offer Changes:</strong> Businesses may modify or withdraw offers without prior notice</li>
              <li><strong>Redemption:</strong> Some offers may have restrictions, expiration dates, or limited availability</li>
            </ul>

            <h2>2. Financial Risks</h2>
            <p>
              <strong>Payment Processing:</strong> All payments are processed through secure third-party providers. 
              While we use industry-standard security measures, we cannot guarantee complete protection against 
              unauthorized transactions or data breaches.
            </p>
            
            <p>
              <strong>Refund Limitations:</strong> Refunds are subject to our Terms of Sale and may not be 
              available for all services or circumstances.
            </p>

            <h2>3. Technology Risks</h2>
            <ul>
              <li><strong>Service Interruptions:</strong> Our platform may experience downtime due to maintenance, 
              technical issues, or external factors beyond our control</li>
              <li><strong>Data Loss:</strong> While we maintain backups, there is always a risk of data loss or corruption</li>
              <li><strong>Compatibility:</strong> Our services may not be compatible with all devices or browsers</li>
              <li><strong>Third-Party Integrations:</strong> We rely on third-party services that may affect our platform's functionality</li>
            </ul>

            <h2>4. User-Generated Content Risks</h2>
            <p>
              Our platform allows user reviews and content submission:
            </p>
            <ul>
              <li><strong>Content Accuracy:</strong> User-generated content reflects individual opinions and experiences</li>
              <li><strong>Moderation:</strong> While we moderate content, inappropriate material may occasionally appear</li>
              <li><strong>Liability:</strong> Users are responsible for the content they post</li>
            </ul>

            <h2>5. Privacy and Data Risks</h2>
            <ul>
              <li><strong>Data Collection:</strong> Using our services involves sharing personal information</li>
              <li><strong>Third-Party Sharing:</strong> Some data may be shared with business partners for service delivery</li>
              <li><strong>Security Breaches:</strong> Despite security measures, no system is completely secure</li>
              <li><strong>Location Data:</strong> Location-based services may reveal your whereabouts</li>
            </ul>

            <h2>6. Business Partnership Risks</h2>
            <p>
              For businesses using our platform:
            </p>
            <ul>
              <li><strong>Visibility:</strong> Listing placement and visibility may vary based on various factors</li>
              <li><strong>Competition:</strong> Your business will be listed alongside competitors</li>
              <li><strong>Review System:</strong> Your business may receive public reviews and ratings</li>
              <li><strong>Platform Changes:</strong> We may modify platform features or policies</li>
            </ul>

            <h2>7. Legal and Regulatory Risks</h2>
            <ul>
              <li><strong>Compliance:</strong> Users and businesses must comply with applicable laws and regulations</li>
              <li><strong>Jurisdiction:</strong> Legal disputes are subject to UK law and jurisdiction</li>
              <li><strong>Regulatory Changes:</strong> Changes in laws may affect our services</li>
            </ul>

            <h2>8. Market and Economic Risks</h2>
            <ul>
              <li><strong>Economic Conditions:</strong> Economic downturns may affect business operations and offers</li>
              <li><strong>Market Changes:</strong> Consumer behavior and market conditions may impact service relevance</li>
              <li><strong>Business Failures:</strong> Listed businesses may cease operations without notice</li>
            </ul>

            <h2>9. Limitation of Liability</h2>
            <p>
              <strong>Maximum Liability:</strong> Our total liability for any claims is limited to the amount 
              paid for our services in the 12 months preceding the claim.
            </p>
            
            <p>
              <strong>Excluded Damages:</strong> We are not liable for indirect, consequential, or punitive 
              damages, including lost profits or business interruption.
            </p>

            <h2>10. Risk Mitigation</h2>
            <p>To minimize risks when using our platform:</p>
            <ul>
              <li>Verify business information independently before visiting or making purchases</li>
              <li>Read offer terms and conditions carefully</li>
              <li>Keep your account information secure</li>
              <li>Report any issues or concerns promptly</li>
              <li>Use secure internet connections when accessing our platform</li>
            </ul>

            <h2>11. Acknowledgment</h2>
            <p>
              By using our services, you acknowledge that you have read, understood, and accept these risks. 
              If you do not accept these risks, please discontinue use of our platform.
            </p>

            <h2>12. Contact Information</h2>
            <p>
              For questions about this Risk Disclosure Statement, contact us at order@shoppersdiscount.club
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};